/**
 * Sidebar — NOC v3 (UI Rework Global).
 *
 * Navegação lateral esquerda, recolhível, agrupada.
 *
 *  - Expandido: 260px — ícone + label + grupos com cabeçalho.
 *  - Recolhido: 72px — só ícones, tooltip no hover.
 *  - Hover-para-abrir: com a sidebar recolhida, passar o mouse a
 *    expande temporariamente (sem mexer no estado salvo); ao sair,
 *    ela recolhe de novo. O toggle por clique fixa a preferência.
 *  - Painel de favoritos no topo (views marcadas com estrela).
 *  - Badge vermelho de alertas no item Alertas.
 *  - Mobile: vira um drawer sobreposto (controlado pelo AppShell).
 *
 * NÃO altera as views — só dispara setView(view).
 */
import { useState } from "react";
import NavIcon from "../../nav/NavIcon.jsx";
import { NAV_GROUPS } from "../../nav/navConfig.js";
import { t } from "../../i18n/index.js";
import { navItemVisible } from "../../lib/rbac.js";

// S3.9E — visibilidade de item de nav por role/capability centralizada em
// rbac.js (navItemVisible), compartilhada com GlobalSearch/ShellTopbar.

export default function Sidebar({
  currentView,
  setView,
  collapsed,
  onToggleCollapse,
  alertsOpen = 0,
  favorites = [],
  onToggleFavorite,
  mobileOpen = false,
  onCloseMobile,
}) {
  // Hover-para-abrir: quando a sidebar está recolhida, o hover a
  // expande visualmente. `hovered` é só visual — não persiste.
  const [hovered, setHovered] = useState(false);
  // Largura efetiva: recolhida só se collapsed E não em hover.
  const showCollapsed = collapsed && !hovered;

  // Itens favoritados — resolve a view → item para montar o painel.
  const favItems = [];
  for (const group of NAV_GROUPS) {
    for (const it of group.items) {
      if (favorites.includes(it.view) && navItemVisible(it)) favItems.push(it);
    }
  }

  return (
    <>
      {/* Backdrop mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside
        onMouseEnter={() => collapsed && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={
          "ipwd-sidebar flex flex-col z-50 transition-all "
          + "duration-200 "
          + (showCollapsed ? "w-[72px]" : "w-[260px]")
          + " "
          + (mobileOpen
              ? "fixed inset-y-0 left-0 translate-x-0"
              : "fixed inset-y-0 left-0 -translate-x-full "
                + "lg:translate-x-0 "
                // Recolhida: SEMPRE lg:fixed (sai do fluxo) — o
                // spacer de 72px no AppShell reserva o espaço.
                // Assim o hover-expand 72→260px sobrepõe o
                // conteúdo sem empurrá-lo (sem reflow/distorção).
                // Expandida: lg:static, ocupa os 260px no fluxo.
                + (collapsed
                    ? "lg:fixed " + (hovered ? "lg:shadow-2xl" : "")
                    : "lg:static"))
        }
      >
        {/* Marca + toggle */}
        <div className="flex items-center gap-2 px-3 h-14 border-b
                        border-noc-border shrink-0">
          <Logo />
          {!showCollapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-noc-text font-bold text-sm
                              tracking-wide leading-tight truncate">
                IP WATCHDOG
              </div>
              <div className="font-mono text-[9px] tracking-[0.22em]
                              text-noc-accent mt-0.5">
                NOC // v3
              </div>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            className="hidden lg:flex items-center justify-center
                       w-7 h-7 rounded-md text-noc-muted
                       hover:text-noc-text hover:bg-noc-panel2
                       transition-colors shrink-0"
            title={collapsed ? t("shell.expand") : t("shell.collapse")}
          >
            <NavIcon name={collapsed ? "expand" : "collapse"} size={16} />
          </button>
          <button
            onClick={onCloseMobile}
            className="lg:hidden flex items-center justify-center
                       w-7 h-7 rounded-md text-noc-muted
                       hover:text-noc-text shrink-0"
          >
            <NavIcon name="close" size={16} />
          </button>
        </div>

        {/* Navegação rolável */}
        <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
          {/* Favoritos */}
          {favItems.length > 0 && (
            <div className="mb-2">
              {!showCollapsed && (
                <div className="px-2 pb-1 flex items-center gap-1.5
                                font-mono text-[9px] uppercase
                                tracking-[0.2em] text-noc-faint">
                  <NavIcon name="star" size={11} />
                  {t("shell.favorites")}
                </div>
              )}
              {favItems.map((it) => (
                <NavItem
                  key={"fav-" + it.view}
                  item={it}
                  active={currentView === it.view}
                  collapsed={showCollapsed}
                  badge={it.badge === "alerts" ? alertsOpen : 0}
                  isFavorite
                  onClick={() => { setView(it.view); onCloseMobile?.(); }}
                  onToggleFavorite={() => onToggleFavorite?.(it.view)}
                />
              ))}
              <div className="my-2 border-t border-noc-border/60" />
            </div>
          )}

          {/* Grupos */}
          {NAV_GROUPS.map((group) => {
            // S3.6B — não renderiza grupo sem itens visíveis para a role.
            const visItems = group.items.filter(navItemVisible);
            if (visItems.length === 0) return null;
            return (
            <div key={group.id} className="mb-1">
              {!showCollapsed && (
                <div className="px-2 pt-2 pb-1 font-mono text-[9px]
                                uppercase tracking-[0.2em]
                                text-noc-faint">
                  {t(group.labelKey)}
                </div>
              )}
              {showCollapsed && group.id !== "overview" && (
                <div className="mx-2 my-1.5 border-t
                                border-noc-border/50" />
              )}
              {visItems.map((it) => (
                <NavItem
                  key={it.view}
                  item={it}
                  active={currentView === it.view}
                  collapsed={showCollapsed}
                  badge={it.badge === "alerts" ? alertsOpen : 0}
                  isFavorite={favorites.includes(it.view)}
                  onClick={() => { setView(it.view); onCloseMobile?.(); }}
                  onToggleFavorite={() => onToggleFavorite?.(it.view)}
                />
              ))}
            </div>
            );
          })}
        </nav>

        {/* Rodapé da sidebar */}
        {!showCollapsed && (
          <div className="px-3 py-2 border-t border-noc-border
                          font-mono text-[9px] tracking-[0.1em]
                          text-noc-faint shrink-0">
            v{import.meta.env?.VITE_APP_VERSION || "2.6.1"}
            <span className="mx-1.5 opacity-40">·</span>
            Fibra Net
          </div>
        )}
      </aside>
    </>
  );
}

/**
 * Item de navegação. No estado recolhido, mostra só o ícone com
 * tooltip nativo. A estrela de favorito aparece no hover (expandido).
 */
function NavItem({
  item, active, collapsed, badge, isFavorite,
  onClick, onToggleFavorite,
}) {
  const showCollapsed = collapsed;
  const label = t(item.labelKey);
  return (
    <div className="group relative">
      <button
        onClick={onClick}
        title={collapsed ? label : undefined}
        className={
          "w-full flex items-center gap-2.5 rounded-lg "
          + "text-sm transition-colors relative "
          + (showCollapsed ? "px-0 py-2 justify-center" : "px-2 py-1.5")
          + " "
          + (active
              ? "ipwd-nav-active text-noc-accent "
                + "border border-noc-accent/25"
              : "text-noc-muted hover:text-noc-text "
                + "hover:bg-noc-panel2 border border-transparent")
        }
      >
        <span className="relative shrink-0">
          <NavIcon name={item.icon} size={18} />
          {/* badge no modo recolhido fica sobre o ícone */}
          {collapsed && badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[15px]
                             h-[15px] px-1 rounded-[5px] bg-noc-err
                             text-noc-bg font-mono text-[9px] font-bold
                             leading-[15px] text-center">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </span>
        {!showCollapsed && (
          <>
            <span className="flex-1 text-left truncate">{label}</span>
            {badge > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-[5px]
                               bg-noc-err/15 text-noc-err border
                               border-noc-err/40 font-mono text-[10px]
                               font-bold leading-[16px] text-center">
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </>
        )}
      </button>

      {/* Estrela de favorito — só no expandido, aparece no hover */}
      {!showCollapsed && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          className={
            "absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 "
            + "flex items-center justify-center rounded "
            + "transition-opacity "
            + (isFavorite
                ? "opacity-100 text-noc-warn"
                : "opacity-0 group-hover:opacity-100 "
                  + "text-noc-muted hover:text-noc-warn")
          }
          title={t("shell.toggleFavorite")}
        >
          <NavIcon name="star" size={13} />
        </button>
      )}
    </div>
  );
}

function Logo() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none"
         xmlns="http://www.w3.org/2000/svg" className="shrink-0">
      <rect width="32" height="32" rx="7" fill="#0C1B2E"
            stroke="#1C3650" strokeWidth="1" />
      <circle cx="16" cy="16" r="5" fill="none"
              stroke="#2BD9F0" strokeWidth="2" />
      <circle cx="16" cy="16" r="2" fill="#2BD9F0" />
      <path d="M3 16 H8 M24 16 H29 M16 3 V8 M16 24 V29"
            stroke="#2BD9F0" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
